import React, { useEffect, useMemo, useState } from "react";

import { getResults, updateResult } from "../api/adminclient";
import AdminSidebar from "./AdminSidebar";
import AdminTopbar from "./Admintopbar";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

export default function AdminResults() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    loadResults();
  }, []);

  const loadResults = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await getResults();

      setResults(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to load results:", err);

      if (err?.response?.status === 401) {
        setError("Your admin session has expired. Please log in again.");
      } else {
        setError("Could not load results from the server.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [results.length, pageSize]);

  const releaseResult = async (result) => {
    try {
      setError("");

      await updateResult(result.id, {
        published: true,
      });

      setResults((currentResults) =>
        currentResults.map((item) =>
          item.id === result.id
            ? {
                ...item,
                published: true,
              }
            : item
        )
      );
    } catch (err) {
      console.error("Failed to publish result:", err);

      if (err?.response?.status === 401) {
        setError("Your admin session has expired. Please log in again.");
      } else {
        setError("Could not publish this result.");
      }
    }
  };

  const totalPages = Math.max(
    1,
    Math.ceil(results.length / pageSize)
  );

  const safePage = Math.min(page, totalPages);

  const pagedResults = useMemo(() => {
    const start = (safePage - 1) * pageSize;

    return results.slice(start, start + pageSize);
  }, [results, safePage, pageSize]);

  const rangeStart =
    results.length === 0
      ? 0
      : (safePage - 1) * pageSize + 1;

  const rangeEnd = Math.min(
    safePage * pageSize,
    results.length
  );

  const goToPage = (p) => {
    setPage(
      Math.min(
        Math.max(1, p),
        totalPages
      )
    );
  };

  const pageNumbers = useMemo(() => {
    const nums = [];

    const add = (n) => {
      if (
        n >= 1 &&
        n <= totalPages &&
        !nums.includes(n)
      ) {
        nums.push(n);
      }
    };

    add(1);
    add(safePage - 1);
    add(safePage);
    add(safePage + 1);
    add(totalPages);

    return nums.sort((a, b) => a - b);
  }, [safePage, totalPages]);

  return (
    <div className="admin-page-full d-flex">
      <AdminSidebar />

      <div className="admin-content">
        <AdminTopbar />

        <h2 className="h5 fw-bold mb-3">
          Results
        </h2>

        {error && (
          <div className="alert alert-warning py-2 small">
            {error}
          </div>
        )}

        <div className="admin-card p-3 p-md-4">
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 ms-auto">
              <label className="small text-muted mb-0">
                Rows per page
              </label>

              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={pageSize}
                onChange={(e) =>
                  setPageSize(Number(e.target.value))
                }
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading && (
            <p className="text-muted mb-0">
              Loading results...
            </p>
          )}

          {!loading && results.length === 0 && (
            <p className="text-muted small mb-0">
              No results recorded yet.
            </p>
          )}

          {!loading && results.length > 0 && (
            <>
              <div className="table-responsive">
                <table className="table align-middle admin-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Student</th>
                      <th>Exam</th>
                      <th>Score</th>
                      <th>Status</th>
                      <th>Available</th>
                      <th className="text-end">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {pagedResults.map((result, i) => {
                      const total =
                        Number(result.total) || 0;

                      const score =
                        Number(result.score) || 0;

                      const percent =
                        total > 0
                          ? Math.round(
                              (score / total) * 100
                            )
                          : 0;

                      const status =
                        result.status ||
                        result.result ||
                        "";

                      const passed =
                        String(status).toUpperCase() ===
                        "PASS";

                      const published =
                        result.published === true;

                      return (
                        <tr key={result.id}>
                          <td className="text-muted">
                            {(safePage - 1) *
                              pageSize +
                              i +
                              1}
                          </td>

                          <td className="fw-semibold">
                            {result.studentName || "—"}
                          </td>

                          <td>
                            {result.examName || "—"}
                          </td>

                          <td>
                            {score}/{total} ({percent}%)
                          </td>

                          <td>
                            <span
                              className={`admin-status-pill ${
                                passed
                                  ? "active"
                                  : "inactive"
                              }`}
                            >
                              {status || "—"}
                            </span>
                          </td>

                          <td>
                            <span
                              className={`admin-status-pill ${
                                published
                                  ? "active"
                                  : "pending"
                              }`}
                            >
                              {published
                                ? "Published"
                                : "Processing"}
                            </span>
                          </td>

                          <td className="text-end">
                            {!published && (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() =>
                                  releaseResult(result)
                                }
                              >
                                Release
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
                <div className="small text-muted">
                  Showing {rangeStart}–{rangeEnd} of{" "}
                  {results.length}
                </div>

                <nav aria-label="Results pagination">
                  <ul className="pagination pagination-sm mb-0">
                    <li
                      className={`page-item ${
                        safePage === 1
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={() =>
                          goToPage(safePage - 1)
                        }
                      >
                        Prev
                      </button>
                    </li>

                    {pageNumbers.map((n, idx) => {
                      const previous =
                        pageNumbers[idx - 1];

                      const showEllipsis =
                        previous !== undefined &&
                        n - previous > 1;

                      return (
                        <React.Fragment key={n}>
                          {showEllipsis && (
                            <li className="page-item disabled">
                              <span className="page-link">
                                …
                              </span>
                            </li>
                          )}

                          <li
                            className={`page-item ${
                              n === safePage
                                ? "active"
                                : ""
                            }`}
                          >
                            <button
                              type="button"
                              className="page-link"
                              onClick={() =>
                                goToPage(n)
                              }
                            >
                              {n}
                            </button>
                          </li>
                        </React.Fragment>
                      );
                    })}

                    <li
                      className={`page-item ${
                        safePage === totalPages
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={() =>
                          goToPage(safePage + 1)
                        }
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}